"use client"

import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import axios from 'axios'

const UserContext = createContext(undefined)

export function UserProvider({ children, initialUsers = [], initialCurrentUser = null }) {
  
  const [users, setUsers] = useState(initialUsers)
  const [currentUser, setCurrentUser] = useState(initialCurrentUser)
  
  // Use refs to track the last values to avoid infinite re-renders
  const lastInitialUsersRef = useRef()
  const lastInitialCurrentUserRef = useRef()

  // Update users when initialUsers prop changes
  useEffect(() => {
    const currentInitialUsers = JSON.stringify(initialUsers)
    if (lastInitialUsersRef.current !== currentInitialUsers) {
      lastInitialUsersRef.current = currentInitialUsers
      setUsers(initialUsers)
    }
  }, [initialUsers])

  // Update current user when initialCurrentUser prop changes
  useEffect(() => {
    const currentInitialUser = JSON.stringify(initialCurrentUser)
    if (lastInitialCurrentUserRef.current !== currentInitialUser) {
      lastInitialCurrentUserRef.current = currentInitialUser
      setCurrentUser(initialCurrentUser)
    }
  }, [initialCurrentUser])

  // Enhanced setCurrentUser with logging
  const setCurrentUserWithLogging = (user) => {
    setCurrentUser(user);
  };

  const addUser = async (userData) => {
    try {
      const response = await axios.post('/users', userData)
      
      // Refresh the page to get updated data from server
      router.reload({ only: ['users'] })
      
      return { success: true, data: response.data }
    } catch (error) {
      console.error('Error adding user:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to add user' 
      }
    }
  }

  const updateUser = async (updatedUser) => {
    try {
      const response = await axios.put(`/users/${updatedUser.id}`, updatedUser)
      
      // Refresh the page to get updated data from server
      router.reload({ only: ['users'] })
      
      return { success: true, data: response.data }
    } catch (error) {
      console.error('Error updating user:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update user' 
      }
    }
  }

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`/users/${userId}`)
      
      // Immediately update local state by removing the deleted user
      setUsers(prevUsers => {
        const updatedUsers = prevUsers.filter(user => user.id !== userId)
        return updatedUsers
      })
      
      return { success: true }
    } catch (error) {
      console.error('Error deleting user:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to delete user' 
      }
    }
  }

  return (
    <UserContext.Provider
      value={{
        users,
        currentUser,
        addUser,
        updateUser,
        deleteUser,
        setCurrentUser: setCurrentUserWithLogging,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUsers() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUsers must be used within a UserProvider')
  }
  return context
} 